/**
 * Business project registry — the Beast Command Center is a controller for
 * PROJECTS (Boss, 2026-07-07). Each business project owns its websites,
 * funnels, email keys and tool modules. Sidebar "Projects" expands from this;
 * /p/[slug] renders the per-project suite.
 *
 * emailKeys — values of `project` in beast-mailer inbox rows (domain_map).
 * funnelId  — id in funnels.db registry (null = funnel not wired yet).
 */
export type ProjectTab =
  | 'websites' | 'performance' | 'improve' | 'factory'
  | 'funnel' | 'video-lab' | 'ab-tests' | 'ads'
  | 'content' | 'crm' | 'email' | 'investors';

export interface BusinessProject {
  slug: string;
  name: string;
  tagline: string;
  icon: string;              // lucide icon name (see PROJECT_ICONS in Sidebar / layout)
  color: string;             // tailwind text color accent
  funnelId: string | null;
  funnelUrl?: string;        // public funnel site
  adsAccountId?: string;     // Meta ad account id — scopes the Ads tab to this project
  sites: { slug: string; domain: string }[] | 'factory'; // 'factory' = all hub sites
  emailKeys: string[] | 'factory';
  tabs: ProjectTab[];        // ordered tool tabs rendered INSIDE /p/[slug]/[tab]
  defaultTab?: ProjectTab;   // landing tab (e.g. wtf → performance); default = overview
  links?: { label: string; href: string }[];
}

export const BUSINESS_PROJECTS: BusinessProject[] = [
  {
    slug: 'wtf',
    name: 'WTF Ventures',
    tagline: 'Website factory — 50+ niche commerce sites',
    icon: 'Factory',
    color: 'text-cyan-400',
    funnelId: null,
    sites: 'factory',
    emailKeys: 'factory',
    tabs: ['performance', 'improve', 'factory', 'content', 'crm', 'email'],
    defaultTab: 'performance', // Boss: "WTF opens on performance, all the websites, that's the most important"
    links: [{ label: 'Sites hub', href: 'https://app.anydomain.ca' }],
  },
  // 'charters' (Private Charters) removed 2026-08-31 — Boss: "can you just
  // leave Amigo instead of private charters". The four sites live on the
  // collab-amigo entry; the 'charters' EMAIL key survives on its emailKeys.
  {
    slug: 'olga',
    name: 'Olga Newman',
    tagline: 'ERM Method — VSL funnel, quiz, drip, club',
    icon: 'Flower2',
    color: 'text-pink-400',
    funnelId: 'olga-erm',
    funnelUrl: 'https://yevlakhova.com',
    adsAccountId: '2177343859742537',   // Meta "Olga ERM"
    sites: [
      { slug: 'olgagoldman', domain: 'olgagoldman.com' },
      { slug: 'olganewman', domain: 'olganewman.com' },
      { slug: 'yevlakhova', domain: 'yevlakhova.com' },
    ],
    emailKeys: ['olga'],
    tabs: ['improve', 'funnel', 'video-lab', 'ab-tests', 'ads', 'websites', 'email'],
    defaultTab: 'funnel',   // overview KPIs embedded in Funnel tab — no standalone Overview
  },
  {
    // Boss 2026-07-07: "Anna Raight IS AstroForYou" — one project, one funnel.
    slug: 'astro',
    name: 'AstroForYou',
    tagline: 'Anna Raight — astrology VSL funnel',
    icon: 'MoonStar',
    color: 'text-indigo-400',
    funnelId: 'anna-raight',
    funnelUrl: 'https://annaraight.com',
    adsAccountId: '25296222293328588',  // Meta "AstroForYou School #1"
    sites: [],
    emailKeys: ['astroforyou', 'anna'],
    tabs: ['funnel', 'video-lab', 'ab-tests', 'ads', 'email'],
  },
  {
    slug: 'vctronics',
    name: 'VCTronics',
    tagline: 'Robotics R&D — investor pitch + outreach',
    icon: 'Bot',
    color: 'text-green-400',
    funnelId: null,
    sites: [{ slug: 'vctronics-pitch', domain: 'pitch.vctronics.com' }],
    emailKeys: ['vctronics'],
    tabs: ['investors', 'email', 'content'],
    defaultTab: 'investors',
    links: [
      { label: 'Pitch site', href: 'https://pitch.vctronics.com' },
      { label: 'Outreach admin', href: 'https://pitch.vctronics.com/admin/outreach' },
    ],
  },
  // Boss 2026-07-13: Ivan is a REAL project now (industrial.film is live + GSC-attached),
  // so he's promoted to a top-level category right after VCTronics — same full tab suite as
  // WTF Ventures, expands in the sidebar into all his website info. Keeps the `collab-ivan`
  // slug so ventures/ivan disk + DB routing stays intact; only the placeholders (Brian, Homiak,
  // Timur) remain collapsed under the 'Collabs' group (see COLLAB_PLACEHOLDERS below).
  {
    slug: 'collab-ivan',
    name: 'Ivan',
    tagline: 'industrial.film + game tables — Ivan\'s sites',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    // Boss 2026-07-21: actualise Ivan's launched sites — gametables.ca + gametables.us
    // went live (verified HTTP 200 + real Ivan content). a14.ca excluded (foreign spam,
    // not ours); gamingtables.ca excluded (blank page); web3growthagency.com not resolving yet.
    sites: [
      { slug: 'industrial-film', domain: 'industrial.film' },
      { slug: 'gametables-ca', domain: 'gametables.ca' },
      { slug: 'gametables-us', domain: 'gametables.us' },
      { slug: 'a14-brand-systems', domain: 'brandsystems.studio' },
      { slug: 'a14-product-systems', domain: 'productsystems.studio' },
    ],
    emailKeys: [],
    tabs: ['performance', 'improve', 'factory', 'content', 'crm', 'email'],
    defaultTab: 'performance',
    links: [
      { label: 'industrial.film', href: 'https://industrial.film' },
      { label: 'gametables.ca', href: 'https://gametables.ca' },
      { label: 'gametables.us', href: 'https://gametables.us' },
      { label: 'brandsystems.studio', href: 'https://brandsystems.studio' },
      { label: 'productsystems.studio', href: 'https://productsystems.studio' },
    ],
  },
  // ── COLLABS (Boss 2026-07-07): ONE 'Collabs' group in the sidebar for people whose
  // process hasn't started yet. Each person is a project at /p/collab-<name>; the Sidebar
  // renders them nested under 'Collabs'. Ivan graduated out of here (promoted above).
  {
    slug: 'collab-brian',
    name: 'Brian',
    tagline: 'placeholder — no assets attached yet',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    sites: [],
    emailKeys: [],
    tabs: ['performance'],
  },
  {
    slug: 'collab-homiak',
    name: 'Homiak',
    tagline: 'placeholder — no assets attached yet',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    sites: [],
    emailKeys: [],
    tabs: ['performance'],
  },
  {
    slug: 'collab-timur',
    name: 'Timur',
    tagline: 'placeholder — no assets attached yet',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    sites: [],
    emailKeys: [],
    tabs: ['performance'],
  },
  {
    slug: 'collab-katia',
    name: 'Katia',
    tagline: 'placeholder — no assets attached yet',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    sites: [],
    emailKeys: [],
    tabs: ['performance'],
  },
  {
    slug: 'collab-yakov',
    name: 'Yakov',
    tagline: 'placeholder — no assets attached yet',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    sites: [],
    emailKeys: [],
    tabs: ['performance'],
  },
  {
    slug: 'collab-amigo',
    name: 'Amigo',
    // The one collab for the private-charter business (Boss, 2026-08-31:
    // "we need only one collab Amigo and all private charter projects belong
    // there"). Real trees: projects/business/ventures/amigo/<slug>.
    tagline: 'Private charters — jets & yachts, 4 sites (FR)',
    icon: 'Plane',
    color: 'text-rose-400',
    funnelId: null,
    sites: [
      { slug: 'aviationprivee-fr', domain: 'aviationprivee.fr' },
      { slug: 'yachtcannes-fr', domain: 'yachtcannes.fr' },
      { slug: 'yachtmonaco-fr', domain: 'yachtmonaco.fr' },
      { slug: 'yachtsainttropez-fr', domain: 'yachtsainttropez.fr' },
    ],
    emailKeys: ['charters'],
    tabs: ['performance', 'websites', 'content', 'crm', 'email'],
    defaultTab: 'performance',
  },
  {
    slug: 'collab-edik',
    name: 'Edik',
    tagline: 'placeholder — no assets attached yet',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    sites: [],
    emailKeys: [],
    tabs: ['performance'],
  },
  {
    slug: 'collab-tommy',
    name: 'Tommy',
    tagline: 'placeholder — no assets attached yet',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    sites: [],
    emailKeys: [],
    tabs: ['performance'],
  },
  {
    slug: 'collab-dima',
    name: 'Dima',
    tagline: 'placeholder — no assets attached yet',
    icon: 'Users',
    color: 'text-rose-400',
    funnelId: null,
    sites: [],
    emailKeys: [],
    tabs: ['performance'],
  },
];

export const getBusinessProject = (slug: string) =>
  BUSINESS_PROJECTS.find(p => p.slug === slug) || null;

// Collab PEOPLE whose process hasn't started — these stay collapsed under the sidebar's
// 'Collabs' group and keep the collab overview page (projects list + New Project button).
// Everything else, including graduated collabs like Ivan, renders as a top-level project.
// (Boss 2026-07-13: 'take Ivan as a separate category, collapse only Brian, Homiak, Timur'.)
export const COLLAB_PLACEHOLDERS = ['collab-brian', 'collab-homiak', 'collab-timur', 'collab-katia', 'collab-yakov', 'collab-edik', 'collab-tommy', 'collab-dima'];
export const isPlaceholderCollab = (slug: string) => COLLAB_PLACEHOLDERS.includes(slug);

// ── CREATE PLACES ── the ONE picker every "new project" surface shows.
// Mirrors the sidebar structure exactly (Boss 2026-07-09: "everything has to
// match the way we've structured it under the projects"). No raw disk paths
// in the UI — the server maps category/collab to the real tree.
export interface CreatePlace {
  key: string;       // option value
  label: string;     // what Boss sees
  group: 'Businesses' | 'Collabs' | 'Other';
  category: string;  // projects.category sent to POST /api/projects
  collab?: string;   // projects.collab (collab-*) — server routes to ventures/<person>/
}
export const CREATE_PLACES: CreatePlace[] = [
  { key: 'wtf',       label: 'WTF Ventures',     group: 'Businesses', category: 'wtf' },
  { key: 'charters',  label: 'Private Charters', group: 'Businesses', category: 'amigo' },
  { key: 'olga',      label: 'Olga Newman',      group: 'Businesses', category: 'ventures' },
  { key: 'astro',     label: 'AstroForYou',      group: 'Businesses', category: 'ventures' },
  { key: 'vctronics', label: 'VCTronics',        group: 'Businesses', category: 'ventures' },
  ...BUSINESS_PROJECTS.filter(p => p.slug.startsWith('collab-')).map(p => ({
    key: p.slug, label: p.name, group: 'Collabs' as const, category: 'ventures', collab: p.slug,
  })),
  { key: 'tools',    label: 'Tools',             group: 'Other', category: 'tools' },
  { key: 'family',   label: 'Family',            group: 'Other', category: 'family' },
  { key: 'personal', label: 'Personal Projects', group: 'Other', category: 'personal' },
];
export const CREATE_PLACE_GROUPS = ['Businesses', 'Collabs', 'Other'] as const;
